import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useApi } from '../../hooks/useApi'
import { Lock, Loader2, ShieldCheck } from 'lucide-react'

const Login = () => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [needsTOTP, setNeedsTOTP] = useState(false)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()
  const api = useApi()

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!username || !password) { setError('Enter both fields'); return }
    if (needsTOTP && !totpCode) { setError('Enter your 2FA code'); return }

    setLoading(true); setError(null)
    try {
      const body = { username, password }
      if (totpCode) body.totp_code = totpCode

      const result = await api.adminLogin(body.username, body.password, body.totp_code)
      
      if (result.requires_totp) {
        setNeedsTOTP(true)
        setError(null)
        return
      }
      if (result.token) {
        login(result.token)
        navigate('/co')
        return
      }
      setError(result.message || 'Login failed')
    } catch (err) {
      setError(err.message || 'Login failed')
    } finally { setLoading(false) }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="card w-full max-w-sm animate-fade-in">
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-3">
            {needsTOTP ? <ShieldCheck size={20} className="text-accent" /> : <Lock size={20} className="text-accent" />}
          </div>
          <h1 className="text-lg font-bold text-text-primary">
            {needsTOTP ? 'Two-Factor Authentication' : 'Admin Sign In'}
          </h1>
          <p className="text-text-muted text-sm mt-0.5">
            {needsTOTP ? 'Enter the code from your authenticator app' : 'G2Bulk Redeem System'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!needsTOTP ? (
            <>
              <div>
                <label className="label mb-1 block">Username</label>
                <input type="text" value={username} onChange={(e) => { setUsername(e.target.value); setError(null) }} placeholder="admin" className="input" autoFocus />
              </div>
              <div>
                <label className="label mb-1 block">Password</label>
                <input type="password" value={password} onChange={(e) => { setPassword(e.target.value); setError(null) }} placeholder="••••••••" className="input" />
              </div>
            </>
          ) : (
            <div>
              <label className="label mb-1 block">2FA Code</label>
              <input
                type="text"
                value={totpCode}
                onChange={(e) => { setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(null) }}
                placeholder="000000"
                className="input text-center text-2xl font-mono tracking-[0.5em] h-14"
                maxLength={6}
                autoFocus
                inputMode="numeric"
              />
            </div>
          )}

          {error && <p className="text-error text-sm text-center">{error}</p>}

          <button type="submit" disabled={loading} className="btn btn-primary w-full">
            {loading ? <><Loader2 size={16} className="animate-spin mr-2" />Signing in...</> : needsTOTP ? 'Verify' : 'Sign In'}
          </button>

          {needsTOTP && (
            <button type="button" onClick={() => { setNeedsTOTP(false); setTotpCode(''); setError(null) }} className="btn btn-secondary w-full">
              Back to login
            </button>
          )}
        </form>

        <div className="mt-4 text-center">
          <a href="/" className="text-text-muted hover:text-text-primary text-sm">← Public site</a>
        </div>
      </div>
    </div>
  )
}

export default Login
